"use client"

import { useId, type ReactNode } from "react"
import { FormField, Input, Select, Textarea } from "../Input"
import { SaveStatus } from "./SaveStatus"
import { useAutosaveField } from "./useAutosaveField"
import { clampNumber } from "./core"

/**
 * Azúcar: un campo etiquetado del DS que autosalva, con `<SaveStatus>` al lado.
 * - texto/textarea → persiste on-blur
 * - número → persiste on-blur, clampeando a [min,max]
 * - select → persiste al cambiar (inmediato)
 *
 * Para guardados de GRUPO (un persist para varios inputs) usá `useAutosaveField`
 * directo; este azúcar es para campos independientes (un persist por campo).
 */
export type AutosaveFieldProps<T extends string | number> = {
  label?: string
  helper?: string
  initial: T
  /** Persiste el valor. DEBE lanzar si falla. */
  persist: (value: T) => Promise<void>
  validate?: (value: T) => string | null
  onError?: (message: string) => void
  disabled?: boolean
  className?: string
} & (
  | { type?: "text"; placeholder?: string }
  | { type: "textarea"; placeholder?: string; rows?: number }
  | { type: "number"; min?: number; max?: number; step?: number }
  | { type: "select"; options: { value: string; label: string }[] }
)

export function AutosaveField<T extends string | number>(props: AutosaveFieldProps<T>) {
  const id = useId()
  const type = props.type ?? "text"

  const field = useAutosaveField<T>({
    initial: props.initial,
    persist: props.persist,
    validate: props.validate,
    onError: props.onError,
    mode: type === "select" ? "immediate" : "blur",
  })

  // Acceso permisivo a las props específicas por variante (la unión + genérico no
  // narrowea limpio; el API público sí está tipado, el cast queda contenido acá).
  const p = props as AutosaveFieldProps<T> & {
    placeholder?: string
    rows?: number
    min?: number
    max?: number
    step?: number
    options?: { value: string; label: string }[]
  }

  const errored = field.status === "error"
  const state = errored ? "error" : "default"

  let control: ReactNode
  if (type === "select") {
    control = (
      <Select
        id={id}
        state={state}
        value={field.value as string}
        disabled={props.disabled}
        onChange={(e) => field.saveNow(e.target.value as T)}
      >
        {(p.options ?? []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    )
  } else if (type === "number") {
    control = (
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        state={state}
        value={Number.isNaN(field.value as number) ? "" : (field.value as number)}
        min={p.min}
        max={p.max}
        step={p.step}
        disabled={props.disabled}
        onChange={(e) => field.setValue(Number(e.target.value) as T)}
        onBlur={() => field.saveNow(clampNumber(field.value as number, p.min, p.max) as T)}
      />
    )
  } else if (type === "textarea") {
    control = (
      <Textarea
        id={id}
        state={state}
        value={field.value as string}
        placeholder={p.placeholder}
        rows={p.rows}
        disabled={props.disabled}
        onChange={(e) => field.setValue(e.target.value as T)}
        onBlur={field.onBlur}
      />
    )
  } else {
    control = (
      <Input
        id={id}
        type="text"
        state={state}
        value={field.value as string}
        placeholder={p.placeholder}
        disabled={props.disabled}
        onChange={(e) => field.setValue(e.target.value as T)}
        onBlur={field.onBlur}
      />
    )
  }

  return (
    <FormField
      label={props.label}
      htmlFor={id}
      helper={props.helper}
      error={errored ? (field.error ?? undefined) : undefined}
      className={props.className}
    >
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">{control}</div>
        <SaveStatus status={field.status} error={field.error} />
      </div>
    </FormField>
  )
}
